import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useProfile } from "../context/ProfileContext";
import ResultsOverlay from "../components/ResultsOverlay";
import MascotCoach from "../components/MascotCoach";
import { MASCOTS, gameById, pick } from "../data/gameData";
import { sound } from "../lib/sound";
import { speech } from "../lib/speech";

const PAIRS = 6;

export default function MemoryForge({ tables, subtitle }) {
  const navigate = useNavigate();
  const { profile, refresh, readAloud } = useProfile();
  const game = gameById("memory-forge");
  const mascot = MASCOTS[game.mascot];
  const accent = game.color;

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]); // indices
  const [matched, setMatched] = useState(new Set());
  const [wrongPair, setWrongPair] = useState(null); // [i,j]
  const [coach, setCoach] = useState(mascot.welcome[0]);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const logRef = useRef([]);
  const attemptStart = useRef(Date.now());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.nextQuestions({ profile_id: profile.id, tables, count: PAIRS * 3, choices: 2 }).then((data) => {
      if (!alive) return;
      const built = [];
      const unique = data.questions.filter((qq, idx, all) =>
        all.findIndex((item) => item.product === qq.product) === idx).slice(0, PAIRS);
      unique.forEach((qq, idx) => {
        built.push({ pairId: idx, type: "eq", label: `${qq.d1} × ${qq.d2}`, a: qq.a, b: qq.b });
        built.push({ pairId: idx, type: "prod", label: `${qq.product}`, a: qq.a, b: qq.b });
      });
      for (let i = built.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [built[i], built[j]] = [built[j], built[i]];
      }
      setCards(built); setMatched(new Set()); setSelected([]); setWrongPair(null);
      setFinished(false); setResult(null); logRef.current = [];
      setLoading(false);
    });
    return () => { alive = false; };
  }, [profile.id, JSON.stringify(tables), reloadKey]);

  const submit = async (log) => {
    sound.win();
    const res = await api.submitRound({ profile_id: profile.id, game_type: game.id, answers: log });
    setResult(res); setFinished(true); refresh();
  };

  const onCard = (i) => {
    if (loading || finished) return;
    if (matched.has(i) || selected.includes(i)) return;
    if (selected.length >= 2) return;

    if (selected.length === 0) {
      attemptStart.current = Date.now();
      setSelected([i]);
      sound.click();
      if (readAloud && cards[i].type === "eq") speech.speakEquation(cards[i].a, cards[i].b);
      return;
    }
    const j = selected[0];
    const a = cards[j], b = cards[i];
    setSelected([j, i]);
    const rt = Date.now() - attemptStart.current;
    const isPair = a.pairId === b.pairId && a.type !== b.type;

    if (isPair) {
      logRef.current.push({ a: a.a, b: a.b, correct: true, response_ms: rt });
      setCoach(pick(mascot.correct));
      sound.correct();
      setTimeout(() => {
        const nm = new Set(matched); nm.add(i); nm.add(j);
        setMatched(nm); setSelected([]);
        if (nm.size === cards.length) submit(logRef.current);
      }, 350);
    } else {
      const eq = a.type === "eq" ? a : (b.type === "eq" ? b : a);
      logRef.current.push({ a: eq.a, b: eq.b, correct: false, response_ms: rt });
      setCoach(pick(mascot.wrong));
      sound.wrong();
      setWrongPair([j, i]);
      setTimeout(() => { setWrongPair(null); setSelected([]); }, 800);
    }
  };

  if (loading) {
    return <div className="mr-bg min-h-screen flex items-center justify-center text-white/70 font-bold animate-pulse">Loading Memory Forge…</div>;
  }

  const matchedCount = matched.size / 2;

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{ background: `radial-gradient(900px 500px at 50% -10%, ${accent}33, transparent 60%), linear-gradient(180deg,#0A0B1A,#1a1206 65%,#0A0B1A)` }}
         data-testid="game-screen">
      <div className="relative z-20 px-4 pt-4 max-w-5xl mx-auto flex items-center gap-3">
        <button data-testid="game-exit-btn" onClick={() => navigate("/map")}
                className="w-10 h-10 rounded-full glass flex items-center justify-center press"><X size={20} /></button>
        <div className="flex-1">
          <div className="text-sm font-bold leading-none">{game.name}</div>
          <div className="text-[11px] text-[#A8B2D1]">{subtitle}</div>
        </div>
        <div className="px-3 py-1.5 rounded-full glass font-bold text-sm flex items-center gap-1.5">
          <Sparkles size={15} style={{ color: accent }} /> {matchedCount}/{cards.length / 2}
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 mt-5">
        <div className="text-center text-sm text-[#A8B2D1] mb-4 font-semibold">
          Tap an equation, then its matching product to forge a rune.
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 pb-40">
          {cards.map((c, i) => {
            const isMatched = matched.has(i);
            const isSel = selected.includes(i);
            const isWrong = wrongPair?.includes(i);
            let style = { background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.16)" };
            if (c.type === "eq") style.borderColor = isSel || isWrong || isMatched ? style.borderColor : `${accent}66`;
            if (isSel) style = { background: `${accent}22`, borderColor: accent };
            if (isWrong) style = { background: "rgba(255,51,102,0.22)", borderColor: "#FF3366" };
            if (isMatched) style = { background: "rgba(0,255,102,0.18)", borderColor: "#00FF66" };
            return (
              <motion.button
                key={i}
                data-testid={`forge-card-${i}`}
                onClick={() => onCard(i)}
                disabled={isMatched}
                whileTap={{ scale: 0.94 }}
                animate={isMatched ? { scale: [1, 1.08, 1] } : {}}
                className="press border-2 rounded-2xl h-48 sm:h-52 flex flex-col items-center justify-center relative"
                style={style}>
                <span className="absolute top-1.5 left-2 text-[9px] font-bold uppercase tracking-wide"
                      style={{ color: c.type === "eq" ? accent : "#A8B2D1" }}>
                  {c.type === "eq" ? "▢ eq" : "◇ ans"}
                </span>
                <span className="font-math font-extrabold text-lg sm:text-3xl">{c.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-4 left-4 z-30">
        <MascotCoach mascotId={game.mascot} message={coach} color={accent} />
      </div>

      <AnimatePresence>
        {finished && (
          <ResultsOverlay result={result} accent={accent}
                          onReplay={() => setReloadKey((k) => k + 1)} onExit={() => navigate("/map")} />
        )}
      </AnimatePresence>
    </div>
  );
}
